import { AddCircleOutlineRounded } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import * as CategoryActions from "../../../../redux/categories/categories.actions";
import * as CategoryReducers from "../../../../redux/categories/categories.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import Button3D from "../../../../utils/common/3d-button/Button3D";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import {
  ICategoryResponseView,
  ISubCategoryView,
} from "../../models/ICategoryResponseView";
import { primary } from "../../../../themes/theme";

const validationSchema = Yup.object().shape({
  categoryId: Yup.string().required("Category is required"),
  name: Yup.string().trim().required("Name is required"),
  description: Yup.string().trim().required("Description is required"),
});

const AddCategory: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();

  const [categoryId, setCategoryId] = useState<string>("");
  const [subCategories, setSubCategories] = useState<ISubCategoryView[]>([]);

  // Get all Categories from redux
  const categoryReduxState: CategoryReducers.InitialState = useSelector(
    (state: RootState) => state[CategoryReducers.categoryFeatureKey]
  );

  const { loading, categories } = categoryReduxState;

  useEffect(() => {
    dispatch(CategoryActions.getAllCategoriesAction());
  }, [dispatch]);

  useEffect(() => {
    if (categories.length > 0) {
      const firstCategoryId = categories[0]?._id;
      if (firstCategoryId) {
        setCategoryId(firstCategoryId);
        formik.setFieldValue("categoryId", firstCategoryId);
        setSubCategories(categories[0].subCategories || []);
      }
    }
  }, [categories]);

  const handleCategoryChange = (e: any) => {
    const selectedCategoryId = e.target.value;
    setCategoryId(selectedCategoryId);
    formik.setFieldValue("categoryId", selectedCategoryId);
    if (categories.length > 0) {
      let category: ICategoryResponseView | undefined = categories.find(
        (category) => category?._id?.toString() === selectedCategoryId
      );
      if (category && category.subCategories) {
        setSubCategories(category.subCategories);
      } else {
        setSubCategories([] as ISubCategoryView[]);
      }
    }
  };

  const handleAddSubCategory = async (values: any) => {
    const response = await dispatch(
      CategoryActions.createSubCategoryAction({
        category: values,
        categoryId: categoryId,
      })
    );

    if (response && !response.error) {
      // Directly update the subCategories state for immediate UI update
      setSubCategories((prevSubCategories) => [
        ...prevSubCategories,
        {
          _id: response.payload._id,
          name: values.name,
          description: values.description,
        },
      ]);

      // Refetch all categories in the background
      dispatch(CategoryActions.getAllCategoriesAction());
    }
  };

  const formik = useFormik({
    initialValues: {
      categoryId: "",
      name: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleAddSubCategory(values);
      formik.resetForm();
    },
  });

  return (
    <>
      <Box sx={{ pb: 2, mb: 2 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="Add Sub Category"
          description="Quickly create and organize product categories for streamlined inventory management."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          <Grid
            item
            xs={10}
            md={4}
            component={Paper}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "10px",
              height: "100%",
              position: "sticky",
              top: 90,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Ubuntu",
                  fontSize: "17px",
                  letterSpacing: "1px",
                  fontWeight: "550",
                }}
              >
                | Add Sub Categories
              </Typography>
            </Box>
            <Divider
              sx={{
                mt: 1,
                mb: 2,
                color: primary[900],
                border: "1px dashed #f1f2f3",
              }}
            />
            <form onSubmit={formik.handleSubmit}>
              <FormControl
                variant="outlined"
                fullWidth
                sx={{ minWidth: 120, mb: 2 }}
                size="small"
                error={
                  formik.touched.categoryId && Boolean(formik.errors.categoryId)
                }
              >
                <InputLabel id="demo-select-small-label">Category</InputLabel>
                <Select
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  value={categoryId}
                  label="Category"
                  onChange={handleCategoryChange}
                  defaultValue={""}
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((category, index) => (
                    <MenuItem key={index} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                )}
              </FormControl>
              <FormControl sx={{ minWidth: 120, mb: 2 }} fullWidth>
                <TextField
                  sx={{ mb: 2 }}
                  type="text"
                  placeholder="Sub Category"
                  label="Sub Category"
                  variant="outlined"
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  fullWidth
                  size="small"
                />
                <TextField
                  sx={{ mb: 2 }}
                  id="standard-textarea"
                  multiline
                  rows={4}
                  label="Description"
                  placeholder="Description"
                  variant="outlined"
                  {...formik.getFieldProps("description")}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  fullWidth
                  size="small"
                />
                <Box sx={{ py: 1 }}>
                  <Button3D
                    type="submit"
                    buttonText="Add"
                    buttonIcon={
                      <AddCircleOutlineRounded
                        fontSize="small"
                        sx={{ pr: "5px", pt: "2px", mt: "2px" }}
                      />
                    }
                  />
                </Box>
              </FormControl>
            </form>
          </Grid>

          {/* This Grid is used to show available subcategories in a specific category */}
          <Grid
            item
            xs={10}
            md={6}
            component={Paper}
            elevation={0}
            sx={{ p: 2, borderRadius: "10px" }}
          >
            <Card elevation={0} sx={{ background: "transparent" }}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu",
                    fontSize: "17px",
                    letterSpacing: "1px",
                    fontWeight: "550",
                  }}
                >
                  | Available Sub Categories
                </Typography>
              </Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 0,
                  color: primary[900],
                  border: "1px dashed #f1f2f3",
                }}
              />
              <CardContent>
                <List sx={{ pl: 0, ml: 0 }}>
                  {subCategories.map((subCategory, index) => (
                    <ListItem key={subCategory._id}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          fontFamily: "Ubuntu",
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {subCategory.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Ubuntu" }}
                        >
                          {subCategory.description}
                        </Typography>
                        <Divider variant="middle" sx={{ mt: 2 }} />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AddCategory;
